import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CalendarOff, Plus, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HolidayManagement = ({ user, onLogout }) => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [dateInput, setDateInput] = useState('');
  const [name, setName] = useState('');

  useEffect(() => { fetchHolidays(); }, []);

  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/holidays`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(res.data.holidays || []);
    } catch (e) {
      toast.error('Failed to fetch holidays');
    } finally { setLoading(false); }
  };

  const addHoliday = async (e) => {
    e.preventDefault();
    if (!dateInput) { toast.error('Select a date'); return; }

    // Convert yyyy-mm-dd (HTML date input) to dd-mm-yyyy
    const parts = dateInput.split('-');
    const ddmmyyyy = `${parts[2]}-${parts[1]}-${parts[0]}`;

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BACKEND_URL}/api/holidays`, {
        date: ddmmyyyy, name
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setShowDialog(false);
      setDateInput(''); setName('');
      fetchHolidays();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const removeHoliday = async (id, date) => {
    if (!window.confirm(`Remove holiday on ${date}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/holidays/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Holiday removed');
      fetchHolidays();
    } catch (e) { toast.error('Failed to remove'); }
  };

  // Group holidays by month
  const groupedHolidays = {};
  holidays.forEach(h => {
    try {
      const [d, m, y] = h.date.split('-');
      const dt = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      const key = dt.toLocaleString('default', { month: 'long', year: 'numeric' });
      if (!groupedHolidays[key]) groupedHolidays[key] = [];
      groupedHolidays[key].push(h);
    } catch {
      if (!groupedHolidays['Other']) groupedHolidays['Other'] = [];
      groupedHolidays['Other'].push(h);
    }
  });

  return (
    <div className="flex min-h-screen bg-slate-900" data-testid="holiday-management">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-6 overflow-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <CalendarOff className="w-8 h-8 text-orange-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">Holiday Management</h1>
                <p className="text-slate-400 text-sm">Mark holidays to exclude them from working days, stats & averages</p>
              </div>
            </div>
            <Button onClick={() => setShowDialog(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="add-holiday-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Holiday
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : holidays.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="py-16 text-center">
                <CalendarOff className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">No holidays marked</p>
                <p className="text-slate-500 text-sm mt-1">Add holidays to exclude them from all stats and averages</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedHolidays).map(([monthYear, monthHolidays]) => (
                <div key={monthYear}>
                  <h3 className="text-lg font-semibold text-slate-300 mb-3">{monthYear}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {monthHolidays.map(h => (
                      <Card key={h.id} className="bg-slate-800 border-slate-700 hover:border-orange-500/30 transition-colors">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex flex-col items-center justify-center">
                                <span className="text-orange-400 text-lg font-bold leading-tight">{h.date.split('-')[0]}</span>
                                <span className="text-orange-300 text-[10px] uppercase">{h.day_name?.substring(0, 3)}</span>
                              </div>
                              <div>
                                <p className="text-white font-medium">{h.name}</p>
                                <p className="text-slate-500 text-xs">{h.date}</p>
                              </div>
                            </div>
                            <button onClick={() => removeHoliday(h.id, h.date)} className="text-red-400 hover:text-red-300 p-1" title="Remove">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <Footer />
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CalendarOff className="w-5 h-5 text-orange-400" /> Add Holiday
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={addHoliday} className="space-y-4">
            <div>
              <Label className="text-slate-300">Date *</Label>
              <Input type="date" value={dateInput} onChange={e => setDateInput(e.target.value)} className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Holiday Name (optional)</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Diwali, Independence Day" className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600">Add Holiday</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidayManagement;
